import { useState, FormEvent, useRef } from 'react';

export default function EstimateForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    projectType: '',
    message: ''
  });
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const combined = [...photoFiles, ...files].slice(0, 5);
    setPhotoFiles(combined);

    const previews = combined.map((file) => URL.createObjectURL(file));
    setPhotoPreviews(previews);
  };

  const removePhoto = (index: number) => {
    const updatedFiles = photoFiles.filter((_, i) => i !== index);
    const updatedPreviews = photoPreviews.filter((_, i) => i !== index);
    setPhotoFiles(updatedFiles);
    setPhotoPreviews(updatedPreviews);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const formDataToSend = new URLSearchParams();
    formDataToSend.append('name', formData.name);
    formDataToSend.append('phone', formData.phone);
    formDataToSend.append('projectType', formData.projectType);
    formDataToSend.append('message', formData.message);
    if (photoFiles.length > 0) {
      formDataToSend.append('photos', 'Uncollectable');
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d6lrs1k6fojmejgaj5v0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', phone: '', projectType: '', message: '' });
        setPhotoFiles([]);
        setPhotoPreviews([]);
        if (fileInputRef.current) fileInputRef.current.value = '';
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="estimate" className="py-16 sm:py-20 md:py-24 bg-gradient-to-br from-amber-50 to-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8 sm:mb-10 md:mb-12">
          <span className="text-teal-600 text-sm font-bold tracking-wider uppercase">Free Consultation</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mt-3 mb-3 sm:mb-4 px-4">
            Get a Free Estimate
          </h2>
          <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto px-4">
            Fill out the form below and we'll contact you within 24 hours to discuss your project.
          </p>
        </div>

        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl p-6 sm:p-8 lg:p-10 xl:p-14 border border-slate-100">
          <form id="estimate-form" onSubmit={handleSubmit} data-readdy-form className="space-y-5 sm:space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20 outline-none transition-all text-sm"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20 outline-none transition-all text-sm"
                  placeholder="(586) 443-0294"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Project Type</label>
              <select
                name="projectType"
                value={formData.projectType}
                onChange={(e) => setFormData({ ...formData, projectType: e.target.value })}
                required
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20 outline-none transition-all text-sm cursor-pointer"
              >
                <option value="">Select a project type</option>
                <optgroup label="Remodeling">
                  <option value="Kitchen Remodeling">Kitchen Remodeling</option>
                  <option value="Bathroom Remodeling">Bathroom Remodeling</option>
                  <option value="Basement Finishing">Basement Finishing</option>
                  <option value="Complete Interior Remodeling">Complete Interior Remodeling</option>
                </optgroup>
                <optgroup label="Flooring & Tile">
                  <option value="Flooring Installation">Flooring Installation</option>
                  <option value="Tile Installation">Tile Installation</option>
                </optgroup>
                <optgroup label="Carpentry & Millwork">
                  <option value="Crown Molding">Crown Molding</option>
                  <option value="Wainscoting & Trim">Wainscoting &amp; Trim</option>
                  <option value="Custom Cabinetry">Custom Cabinetry</option>
                  <option value="Interior Doors">Interior Doors</option>
                </optgroup>
                <optgroup label="Finishing Work">
                  <option value="Painting">Painting</option>
                  <option value="Drywall">Drywall</option>
                  <option value="Insulation">Insulation</option>
                </optgroup>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Project Photos
                <span className="text-slate-400 font-normal ml-1">(optional, up to 5)</span>
              </label>
              <div
                onClick={() => fileInputRef.current?.click()}
                className="w-full border-2 border-dashed border-slate-300 hover:border-teal-500 rounded-lg p-5 sm:p-6 text-center cursor-pointer transition-all group"
              >
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 text-slate-400 group-hover:text-teal-500 transition-colors">
                  <i className="ri-image-add-line text-3xl"></i>
                </div>
                <p className="text-sm text-slate-500 group-hover:text-teal-600 transition-colors">
                  Click to upload photos of your project area
                </p>
                <p className="text-xs text-slate-400 mt-1">JPG, PNG, HEIC — max 5 photos</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  name="photos"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoChange}
                  className="hidden"
                />
              </div>

              {photoPreviews.length > 0 && (
                <div className="flex flex-wrap gap-3 mt-4">
                  {photoPreviews.map((src, idx) => (
                    <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden border border-slate-200 shadow-sm">
                      <img src={src} alt={`Preview ${idx + 1}`} className="w-full h-full object-cover object-top" />
                      <button
                        type="button"
                        onClick={() => removePhoto(idx)}
                        className="absolute top-0.5 right-0.5 w-5 h-5 flex items-center justify-center bg-red-500 text-white rounded-full text-xs cursor-pointer hover:bg-red-600 transition-colors"
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Message</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setFormData({ ...formData, message: e.target.value });
                  }
                }}
                rows={5}
                maxLength={500}
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20 outline-none transition-all resize-none text-sm"
                placeholder="Tell us about your project..."
              ></textarea>
              <p className="text-xs text-slate-500 mt-1">{formData.message.length}/500 characters</p>
            </div>

            <div className="flex justify-center pt-2 sm:pt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full sm:w-auto bg-teal-600 text-white px-8 sm:px-10 py-3 sm:py-4 rounded-full font-semibold hover:bg-teal-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center cursor-pointer whitespace-nowrap text-sm sm:text-base"
              >
                {isSubmitting ? 'Submitting...' : 'Request Free Quote'}
                <i className="ri-arrow-right-line ml-2"></i>
              </button>
            </div>

            {submitStatus === 'success' && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg text-sm text-center">
                Thank you! We'll contact you within 24 hours.
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg text-sm text-center">
                Something went wrong. Please call us at 586-443-0294.
              </div>
            )}
          </form>
        </div>

        <div className="text-center mt-6 sm:mt-8">
          <p className="text-slate-600 text-sm px-4">
            Or call us directly at{' '}
            <a href="tel:586-443-0294" className="text-teal-600 font-semibold hover:text-teal-700 cursor-pointer">
              586-443-0294
            </a>
          </p>
        </div>
      </div>
    </section>
  );
}